import PropTypes from 'prop-types';
import React, { Fragment, useState, useEffect } from 'react';
import { Card, Col, Row, Spinner, Modal, Container, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../shared/components/Button';
import './AddressConfirmation.scss';
import axios from 'axios';
import { INFLUENCERS_ENDPOINTS } from '../../../../../config/api'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import AddressModal from '../../../utils/addresses/AddressModal'
import { get, isFunction, isNull } from 'lodash';
import { useSelector } from 'react-redux';

const AddressConfirmation = (
  {
    currentAddress,
    isAddressConfirmed,
    userOfferId,
    onSuccessCallback
  }
) => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    loading: false,
    showAddressUpdateModal: false,
    isAddressConfirmedSet: isAddressConfirmed,
    error: false,
    user: {}
  });
  const userData = useSelector(store => get(store, 'auth.user'));
  const simulatedData = useSelector(store => get(store, 'config.simulatedData'));
  const simulatedUser = useSelector(store => get(store, 'config.simulatedUser'));
  const loadingSimulatedUser = useSelector(store => get(store, 'config.loadingSimulatedUser'));
  const { user } = state

  const formik = useFormik(
    {
      initialValues: {
        addressUpdateScope: null,
        address: {
          id: get(user, 'address.id'),
          first_name: get(user, 'address.first_name') || '',
          last_name: get(user, 'address.last_name') || '',
          address: get(user, 'address.address') || '',
          address_2: get(user, 'address.address_2') || '',
          city: get(user, 'address.city') || '',
          state: get(user, 'address.state') || '',
          zip: get(user, 'address.zip') || '',
          phone: get(user, 'address.phone') || '',
          country: get(user, 'address.country') || '',
        },
      },
      enableReinitialize: true,
      validationSchema: Yup.object({
        addressUpdateScope: Yup.string().nullable().required(t("common.select_option")),
        address: Yup.object({
          first_name: Yup.string()
            .required(`${t('influencers.pages.profile.general.first_name')} ${t('influencers.common.is_required')}`),
          last_name: Yup.string()
            .required(`${t('influencers.pages.profile.general.last_name')} ${t('influencers.common.is_required')}`),
          address: Yup.string()
            .required(`${t('influencers.pages.profile.general.address')} ${t('influencers.common.is_required')}`),
          address_2: Yup.string(),
          city: Yup.string()
            .required(`${t('influencers.pages.profile.general.city')} ${t('influencers.common.is_required')}`),
          state: Yup.string()
            .required(`${t('influencers.pages.profile.general.state')} ${t('influencers.common.is_required')}`),
          zip: Yup.string()
            .required(`${t('influencers.pages.profile.general.zip')} ${t('influencers.common.is_required')}`),
          country: Yup.string()
            .required(`${t('influencers.pages.profile.general.country')} ${t('influencers.common.is_required')}`),
          phone: Yup.string()
            .required(`${t('influencers.pages.profile.general.phone')} ${t('influencers.common.is_required')}`),
          id: Yup.string().nullable()
        })
      }),
      validateOnMount: true,
    }
  );

  useEffect(() => {
    if (!loadingSimulatedUser) {
      const user = simulatedData || userData;
      setState({
        ...state,
        user,
      });
    }
  }, [loadingSimulatedUser]);

  useEffect(() => {
    if (!loadingSimulatedUser && !simulatedUser) {
      setState({
        ...state,
        user: userData,
      });
    }
  }, [simulatedUser]);

  const onCancel = () => setState({ ...state, showAddressChangeChoiceModal: false });

  const onOpenAddressModel = () => {
    setState(
      {
        ...state,
        showAddressChangeChoiceModal: false,
        showAddressUpdateModal: true
      }
    );
  };

  const onSaveForCampaign = () => {
    setState({ ...state, loading: true });

    saveUserOffer(
      {
        ...setUserAddressParamValues(formik.values, 'userOffer'),
        confirm_address: true,
        skip_validation: true,
        address_confirmed: true
      }
    ).then(({ data }) => {
      formik.setFieldValue('address.id', get(data, 'user_offer.address.id') || get(formik.values, 'address.id'));
      setState(
        {
          ...state,
          error: false,
          loading: false,
          showAddressChangeChoiceModal: false,
          showAddressUpdateModal: false,
          isAddressConfirmedSet: true
        }
      );
    }).then(() => {
      if (onSuccessCallback && isFunction(onSuccessCallback))
        onSuccessCallback(fullAddress(get(formik.values, 'address')));
    }).catch(() => {
      setState(
        {
          ...state,
          error: true,
          loading: false,
          showAddressChangeChoiceModal: false,
          showAddressUpdateModal: false
        }
      );
    });
  };

  const onSavePermanentAddress = () => {
    setState({ ...state, loading: true });

    axios.put(INFLUENCERS_ENDPOINTS.USERS.PUT(state.user.id), {
      user: { ...setUserAddressParamValues(formik.values), confirm_address: true }
    }).then(({ data }) => {
      formik.setFieldValue('address.id', get(data, 'user.address.id') || get(formik.values, 'address.id'));
      setState(
        {
          ...state,
          error: false, 
          loading: false,
          showAddressChangeChoiceModal: false,
          showAddressUpdateModal: false,
          isAddressConfirmedSet: true
        }
      );
    }).then(() => {
      saveUserOffer(
        {
          address_confirmed: true,
          skip_validation: true
        }
      ).then(() =>{
        if (onSuccessCallback && isFunction(onSuccessCallback))
          onSuccessCallback(fullAddress(get(formik.values, 'address')));
      })
    }).catch(() => {
      setState(
        {
          ...state,
          error: true, 
          loading: false,
          showAddressChangeChoiceModal: false,
          showAddressUpdateModal: false
        }
      );
    });
  }

  const setUserAddressParamValues = (params, targetObj = 'user') => {
    let userParams = JSON.parse(JSON.stringify(params));

    if (targetObj === 'user') {
      userParams.addresses_attributes = [userParams.address];
      delete userParams.address;
    }

    return userParams;
  };

  const fullAddress = (address) => (
    `${address.first_name} ${address.last_name}\n${address.address}\n${address.city}, ${address.state} ${address.zip}\n${address.country}`
  );

  const isTouched = formik.touched.addressUpdateScope;
  const hasError = formik.errors.addressUpdateScope;
  const addressUpdateOptionsNotSelected = isNull(formik.values.addressUpdateScope);

  if (state.isAddressConfirmedSet) return null;

  const submitAddressConfirmation = (address = null) => {
    setState({ ...state, loading: true });

    saveUserOffer(
      {
        address_confirmed: true,
        skip_validation: true
      }
    ).then(() => {
        setState({
          ...state,
          loading: false,
          isAddressConfirmedSet: true
        })

        if (onSuccessCallback && isFunction(onSuccessCallback))
          onSuccessCallback(address);
      }
    ).catch(error => {
      setState({ ...state, loading: false, error: true });
    });
  };

  const saveUserOffer = (params) => axios.put(INFLUENCERS_ENDPOINTS.USER_OFFERS(userOfferId), { user_offer: params });

  const renderModal = () => (
    <Modal show={state.showAddressChangeChoiceModal} onHide={() => setState({ ...state, showAddressChangeChoiceModal: false })}>
      <Modal.Header closeButton>
        <Modal.Title>{t('influencers.pages.campaign.addresses.update_shipping_address_modal.header')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>{t('influencers.pages.campaign.addresses.update_shipping_address_modal.body')}</p>
        <p className='bold-text'>{t('influencers.pages.campaign.addresses.update_shipping_address_modal.body_bold_warning')}</p>
        <div className='modal-borders modal-address-coonfirmation'>
          <p className='current-address-header'>
            {t('influencers.pages.campaign.addresses.update_shipping_address_modal.current_address')}
          </p>
          <p>{ currentAddress }</p>
        </div>
        <Container className='mt-3'>
          <Row className="no-gutters mb-2">
            <Col xs={12} className='p-0'>
              <Form.Group>
                <div className='modal-borders p-3 d-flex align-items-center'>
                  <input
                    type="radio"
                    id="address-update-campaign"
                    name="addressUpdateScope"
                    value="campaign"
                    checked={formik.values.addressUpdateScope === 'campaign'}
                    onChange={() => formik.setFieldValue('addressUpdateScope', 'campaign')}
                    onBlur={formik.handleBlur}
                  />
                  <label htmlFor="address-update-campaign">
                    <p className='bold-text'>
                      {t('influencers.pages.campaign.addresses.update_shipping_address_modal.update_address_campaign_header')}
                    </p>
                    <p>
                      {t('influencers.pages.campaign.addresses.update_shipping_address_modal.update_address_campaign_body')}
                    </p>
                  </label>
                </div>
                <div className='modal-borders mt-3 p-3 d-flex align-items-center'>
                  <input
                    type="radio"
                    id="address-update-permanent"
                    name="addressUpdateScope"
                    value="permanent"
                    checked={formik.values.addressUpdateScope === 'permanent'}
                    onChange={() => formik.setFieldValue('addressUpdateScope', 'permanent')}
                    onBlur={formik.handleBlur}
                  />
                  <label htmlFor="address-update-permanent">
                    <p className='bold-text'>
                      {t('influencers.pages.campaign.addresses.update_shipping_address_modal.update_permanent_address_header')}
                    </p>
                    <p>
                      {t('influencers.pages.campaign.addresses.update_shipping_address_modal.update_permanent_address_body')}
                    </p>
                  </label>
                </div>
              </Form.Group>
              {
                isTouched && hasError ?
                <Form.Text className="text-danger">
                  {hasError}
                </Form.Text> : null
              }
            </Col>
          </Row>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Row className="no-gutters w-100 justify-content-end">
          <Col xs="auto" className="pr-2">
            <button
              className="btn btn-secondary text-center"
              onClick={() => onCancel()}
            >
              {t("common.cancel")}
            </button>
          </Col>
          <Col xs="auto" className="pl-2">
            <span style={{ cursor: addressUpdateOptionsNotSelected ? 'not-allowed' : 'default' }}>
              <button
                className="btn btn-primary text-center w-auto"
                style={{ width: 'max-content', pointerEvents: addressUpdateOptionsNotSelected ? 'none' : 'auto' }}
                disabled={addressUpdateOptionsNotSelected}
                onClick={() => onOpenAddressModel()}
              >
                {t("influencers.pages.campaign.addresses.update_shipping_address_modal.button_continue")}
              </button>
            </span>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );

  return (
    <Fragment>
      {
        state.loading ?
        <Spinner animation="border" size="sm" /> :
        <div className="my-4">
          <Card className="address-confirmation py-2">
            <Card.Body className="pb-1">
              <Row>
                <Col lg={8}>
                  <div className="text-2 bold-text">
                    { t('influencers.pages.campaign.waiting_product.address_confirmation.title') }
                  </div>
                  <div className="text-2">
                    { t('influencers.pages.campaign.waiting_product.address_confirmation.body') }
                  </div>
                </Col>
                <Col lg={4} className="mt-3 mt-lg-0">
                  <div className="d-flex justify-content-center">
                    <div className="address-confirm-button mr-1">
                      <Button
                        variant="primary"
                        type="button"
                        onClick={submitAddressConfirmation}
                      >
                        { t('influencers.pages.campaign.waiting_product.address_confirmation.yes_button') }
                      </Button>
                    </div>
                    <div className="address-confirm-button">
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={() => setState({ ...state, showAddressChangeChoiceModal: true, showAddressUpdateModal: false })}
                      >
                        { t('influencers.pages.campaign.waiting_product.address_confirmation.show_dialog_button') }
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Modals section */}
          { renderModal() }
          {
            state.showAddressUpdateModal &&
            <AddressModal
              formikForm={formik}
              onSaveCallback={ formik.values.addressUpdateScope === 'permanent' ? onSavePermanentAddress : onSaveForCampaign }
              showAddressUpdateModal={state.showAddressUpdateModal}
              loading={state.loading}
            />
          }
        </div>
      }
    </Fragment>
  );
};

AddressConfirmation.propTypes = {
  currentAddress: PropTypes.string.isRequired,
  isAddressConfirmed: PropTypes.bool.isRequired,
  userOfferId: PropTypes.number.isRequired,
  onSuccessCallback: PropTypes.func
};

export default AddressConfirmation;
